'use client';
import React from 'react';
import { ExclamationTriangleIcon, ArrowPathIcon, InformationCircleIcon } from '@heroicons/react/24/outline';

export type ErrorAlertContext =
  | 'dashboard' |'chart' |'table' |'finance' |'audit' |'users' |'reporting' |'coordinator' |'student' |'sponsor' |'staff' |'generic';

const CONTEXT_HELP: Record<ErrorAlertContext, string> = {
  dashboard: 'Dashboard data could not be loaded. This may be a temporary network issue.',
  chart:      'Chart data failed to load. The underlying data may still be available in the table view.',
  table:      'Table data could not be fetched. Try refreshing or check your network connection.',
  finance:    'Financial records could not be retrieved. Ensure you have the correct permissions and try again.',
  audit:      'Audit log entries could not be loaded. The log is still recording — this is a display issue only.',
  users:      'User list could not be fetched. Verify your admin permissions and retry.',
  reporting:  'Report data failed to load. Filters and exports are still available once data loads.',
  coordinator:'Program or student data could not be retrieved. Check your coordinator assignment and retry.',
  student:    'Your student record could not be loaded. If this persists, contact your campus administrator.',
  sponsor:    'Sponsored student data could not be retrieved. Contact support if this issue continues.',
  staff:      'Course or attendance data could not be loaded. Verify your trainer assignment is active.',
  generic:    'An error occurred while loading data. Please try again.',
};

interface ErrorAlertProps {
  /** The error message to display */
  message: string;
  /** Contextual area — drives the help text shown below the error */
  context?: ErrorAlertContext;
  /** Called when the user clicks "Try Again" */
  onRetry?: () => void;
  /** Whether a retry is currently in progress */
  retrying?: boolean;
  /** Number of retry attempts already made (shows extra help after 2+) */
  retryCount?: number;
  /** Optional override for the help text */
  helpText?: string;
  /** Compact inline variant (no card wrapper) */
  inline?: boolean;
  className?: string;
}

export default function ErrorAlert({
  message,
  context = 'generic',
  onRetry,
  retrying = false,
  retryCount = 0,
  helpText,
  inline = false,
  className = '',
}: ErrorAlertProps) {
  const help = helpText ?? CONTEXT_HELP[context];
  const persistentHelp = retryCount >= 2
    ? 'Multiple retries failed. Please refresh the page or contact support if the problem persists.'
    : null;

  if (inline) {
    return (
      <div
        role="alert"
        aria-live="assertive"
        aria-atomic="true"
        className={`flex items-start gap-3 rounded-xl border border-red-200 bg-red-50 p-4 ${className}`}
      >
        <ExclamationTriangleIcon className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" aria-hidden="true" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-600 text-red-700 leading-snug">{message}</p>
          <p className="text-xs text-red-500 mt-1 leading-relaxed">{persistentHelp ?? help}</p>
        </div>
        {onRetry && (
          <button
            onClick={onRetry}
            disabled={retrying}
            className="flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-red-600 text-white text-xs font-600 hover:bg-red-700 disabled:opacity-60 transition-colors"
            aria-label={retrying ? 'Retrying, please wait' : 'Retry loading data'}
            aria-busy={retrying}
          >
            <ArrowPathIcon className={`w-3.5 h-3.5 ${retrying ? 'animate-spin' : ''}`} aria-hidden="true" />
            {retrying ? 'Retrying…' : 'Retry'}
          </button>
        )}
      </div>
    );
  }

  return (
    <div
      role="alert"
      aria-live="assertive"
      aria-atomic="true"
      className={`flex flex-col items-center justify-center py-10 px-6 text-center ${className}`}
    >
      {/* Icon */}
      <div className="w-14 h-14 rounded-full bg-red-50 flex items-center justify-center mb-4" aria-hidden="true">
        <ExclamationTriangleIcon className="w-7 h-7 text-red-500" />
      </div>

      {/* Heading */}
      <h3 className="text-base font-700 text-gray-900 mb-1">Failed to load data</h3>

      {/* Error message */}
      <p className="text-sm text-red-600 font-500 mb-2 max-w-sm">{message}</p>

      {/* Help text */}
      <div className="flex items-start gap-1.5 max-w-sm mb-5">
        <InformationCircleIcon className="w-4 h-4 text-gray-400 flex-shrink-0 mt-0.5" aria-hidden="true" />
        <p className="text-xs text-gray-500 text-left leading-relaxed">
          {persistentHelp ?? help}
        </p>
      </div>

      {/* Retry button */}
      {onRetry && (
        <button
          onClick={onRetry}
          disabled={retrying}
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-primary text-white text-sm font-600 hover:bg-primary/90 disabled:opacity-60 transition-colors"
          aria-label={retrying ? 'Retrying, please wait' : retryCount > 0 ? `Retry loading data, attempt ${retryCount + 1}` : 'Try loading data again'}
          aria-busy={retrying}
        >
          <ArrowPathIcon className={`w-4 h-4 ${retrying ? 'animate-spin' : ''}`} aria-hidden="true" />
          {retrying ? 'Retrying…' : retryCount > 0 ? `Retry (${retryCount})` : 'Try Again'}
        </button>
      )}
    </div>
  );
}
